import { Box, Group, Image, UnstyledButton, useMantineTheme } from '@mantine/core';
import { IconCircleFilled } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { BatchCustomsStateNotificationType } from '@e-origin/shared';

import { IconFactory } from '@assets';

type NavigateTo = string | ((type: BatchCustomsStateNotificationType) => Promise<string | undefined>);

interface ClickableIndicatorProps {
  type: BatchCustomsStateNotificationType;
  navigateTo?: NavigateTo;
  onIndicatorClick: (e: React.MouseEvent, type: BatchCustomsStateNotificationType) => void;
  children: React.ReactNode;
}

const ClickableIndicator = ({ type, navigateTo, onIndicatorClick, children }: ClickableIndicatorProps) =>
  navigateTo ? <UnstyledButton onClick={(e) => onIndicatorClick(e, type)}>{children}</UnstyledButton> : <>{children}</>;

interface TableNotificationIndicatorProps {
  actionsPending?: number;
  notificationsPending: number;
  comments?: boolean;
  navigateTo?: NavigateTo;
}

export const TableNotificationIndicator: React.FC<TableNotificationIndicatorProps> = ({ actionsPending, notificationsPending, comments, navigateTo }) => {
  const theme = useMantineTheme();
  const navigate = useNavigate();

  const handleIndicatorClick = async (e: React.MouseEvent, type: BatchCustomsStateNotificationType) => {
    e.stopPropagation();

    if (!navigateTo) return;

    const path = typeof navigateTo === 'function' ? await navigateTo(type) : navigateTo;

    if (path) {
      navigate(path);
    }
  };

  if (!actionsPending && !notificationsPending && !comments) {
    return null;
  }

  const hasNotifications = actionsPending || notificationsPending;

  return (
    <Group gap={0} wrap="nowrap">
      <>
        {actionsPending ? (
          <ClickableIndicator type={BatchCustomsStateNotificationType.ACTIONS_PENDING} navigateTo={navigateTo} onIndicatorClick={handleIndicatorClick}>
            <IconCircleFilled size={20} color={theme.colors.red[8]} />
          </ClickableIndicator>
        ) : null}

        {notificationsPending ? (
          <Box ml={actionsPending ? -10 : 0} lh={0}>
            <ClickableIndicator type={BatchCustomsStateNotificationType.NOTIFICATIONS_PENDING} navigateTo={navigateTo} onIndicatorClick={handleIndicatorClick}>
              <IconCircleFilled size={20} color={theme.colors.yellow[5]} />
            </ClickableIndicator>
          </Box>
        ) : null}
      </>

      {comments ? (
        <Box ml={hasNotifications ? 5 : 0} lh={0}>
          <ClickableIndicator type={BatchCustomsStateNotificationType.COMMENTS} navigateTo={navigateTo} onIndicatorClick={handleIndicatorClick}>
            <Image src={IconFactory.comment} alt="Comments" w={20} h={20} />
          </ClickableIndicator>
        </Box>
      ) : null}
    </Group>
  );
};
